import { getEquityValue, getIncomeSource } from '@/utils/vip';
import {
  getMtxxWebGidValue,
  getPcAbCodeFromCookie,
  getPcGidFromCookie,
  getUrlParam,
  getChannel,
  getUserType,
  getSkuType,
} from '@/utils';
import { getGid } from '@/utils/vip';
import { APP_KEY, session_id_collection_cd } from '@/constants';
import { fetchGid } from '@meitu/gid';
import { getDefaultStore } from 'jotai';
import {
  meidouTotalAtom,
  userInfoAtom,
  vipInfoAtom,
  identificationAtom,
  businessStrategyIdAtom,
  extraTrackDataAtom,
} from '@/store';
import { getJumpQuery } from './jumpQuery';
import { getLanguage } from '@/i18n/constants';

const store = getDefaultStore();

// 缓存初始化前业务触发的埋点事件数据
let statStack: any[] = [];
window._stat_track = (
  name: string,
  options: Partial<{
    [key: string]: any;
  }>
) => {
  statStack.push({ name, options });
};

const initMtstat = async (userId?: number) => {
  // 如果是桌面端内访问订阅页面的话，不需要初始化web SDK，否则会多上报page_start事件
  if (getIncomeSource() !== 'MTXX-WEB') {
    return;
  }

  const uid = userId || window.localStorage.getItem('uid');
  const channelValue = getChannel();
  const firstUrl = decodeURIComponent(
    getUrlParam('first_url') || window.sessionStorage.getItem('first_url') || ''
  );
  const abInfo = decodeURIComponent(getUrlParam('ab_info') || '');

  // 是否PC内嵌订阅场景
  const isPcVip = getIncomeSource() !== 'MTXX-WEB';
  // 是否PC登录回跳场景
  const isPcLogin =
    isPcVip &&
    (getUrlParam('code') || getUrlParam('token')) &&
    getUrlParam('state') !== 'sub' &&
    getUrlParam('client_referer') !== 'sub';

  let module_id = getUrlParam('module_id') || '';
  if (isPcLogin) {
    module_id = 'pc_login';
  } else if (isPcVip) {
    module_id = 'pc_vip';
  }

  const vipInfo = store.get(vipInfoAtom);
  const userInfo = store.get(userInfoAtom);
  const meidouTotal = store.get(meidouTotalAtom);
  const identification = store.get(identificationAtom);
  const jumpQuery = getJumpQuery();
  const businessStrategyId = store.get(businessStrategyIdAtom);
  const extraTrackData = store.get(extraTrackDataAtom);

  const commonParams: any = {
    first_url: firstUrl,
    channel: channelValue,
    is_sdk: getUrlParam('is_sdk') || '',
    company_key: getUrlParam('company_key') || '',
    operate_source: getUrlParam('operate_source') || '',
    oid: getUrlParam('oid') || '',
    title: document.title,
    entrance: getUrlParam('entrance') || '',
    source: decodeURIComponent(getUrlParam('source') || ''),
    second_source: decodeURIComponent(getUrlParam('second_source') || ''),
    third_source: decodeURIComponent(getUrlParam('third_source') || ''),
    vip_type: getUrlParam('vip_type') || '',
    matrix_channel: getUrlParam('matrix_channel') || '', // 目前只有抠图业务使用
    template_id: getUrlParam('template_id') || '',
    session_id_collection_cd: session_id_collection_cd,
    equity: getEquityValue(vipInfo),
    industry: decodeURIComponent(getUrlParam('industry') || ''),
    origin_func: getUrlParam('origin_func') || '',
    user_source: getUrlParam('user_source') || '',
    logo_id: getUrlParam('logo_id') || '',
    user_type: getUserType(),
    residual_meidou: meidouTotal,
    identification,
    is_collaborative_mode: getUrlParam('is_collaborative_mode') || '',
    mtxx_web_gid: getMtxxWebGidValue(),
    utm_info: getUrlParam('utm_info') || '',
    pic_id: getUrlParam('pic_id') || '',
    ...jumpQuery,
    business_strategy_id: businessStrategyId,
    client_language: getLanguage(),
    ...(extraTrackData || {}),
  };

  if (isPcVip) {
    commonParams.mac_addr = await getGid();
  }

  // 初始化埋点
  window.mtstat?.init({
    app_key: APP_KEY, // 业务方申请的统计app_key，需要用字符串
    app_version: '2.0.0', // 业务应用的版本号
    app_language: 'zh-CN', // 业务应用的语言
    env: process.env.REACT_APP_ENV === 'release' ? 'release' : 'test',
    log_level: 0, // 调试信息输出控制 0 只输出关键上报日志，1 输出所有的上报日志
    debugger: true,
    channel: channelValue,
    module_id,
  });

  // 从PC端外跳进入的鞋带参数
  const pcGid = getPcGidFromCookie();
  const pcAbCode = getPcAbCodeFromCookie();
  if (pcGid) {
    window.mtstat?.setGid(pcGid, 'pc');
  }
  if (pcAbCode) {
    commonParams.ab_code = pcAbCode;
  }

  window.mtstat?.setCommonParams(commonParams);
  // 默认先加载本地的uid，主要针对退出登录调到三方登录页面后返回的场景需要第一次更新下uid
  if (uid) {
    window.mtstat?.login(`${uid}`);
  }

  // 通过 WebSDK 设置 ab_info 的信息
  if (abInfo) {
    window.mtstat?.setABInfo(JSON.parse(abInfo));
  }

  window._stat_track = function (
    name: string,
    options: {
      [key: string]: any;
    }
  ) {
    window.mtstat?.track(name, options);
  };

  if (window.mtstat && statStack.length !== 0) {
    statStack.forEach((item) => {
      window.mtstat?.track(item.name, item.options);
    });
    statStack = [];
  }
};

export default initMtstat;
